import { ConfigurationType, PricePart, PriceSegment } from '@icc/shared/types';
import { core } from "@icc/shared";
import { Configuration } from '@icc/configurator/shared';
import { ComplexConfiguratorField } from '@icc/shared/types/complex/ComplexConfiguratorField';
import { SketchData } from '@icc/shared/types/complex/SketchData';
import { ComplexActiveConfiguration } from './ComplexActiveConfiguration';
import { changesInVersion8 } from './converters';

export class ComplexConfiguration implements Configuration {
    name = '';
    $version = 8;
    type: ConfigurationType = 'complex'
    price: number | null = null;
    priceNoMargin: number | null = null;
    priceAfterMultiplier: number | null = null;
    priceAfterMultiplierNoMargin: number | null = null;
    priceAfterDiscounts: number | null = null;
    priceIsGross: boolean | null = null;
    priceTaxRate: number | null = null;
    quantity = 1;
    priceSegments: PriceSegment[] = [];
    priceSegmentsNoMargin: PriceSegment[] = [];
    priceParts: PricePart[] = [];
    pricePartsNoMargin: PricePart[] = [];
    discountGroups = {};
    discountGroupsNoMargin = {};
    pricesRevisions: {
        prices: number;
        priceRasters: number;
        charge: number;
        multiplier: number;
        marketFactor: number;
    } = {
        prices: 0,
        priceRasters: 0,
        charge: 0,
        multiplier: 0,
        marketFactor: 0,
    };
    timeLimit: number | null = null;
    timeLimitsStack = [];
    title = '';
    description = '';
    attachments: any[] = [];
    deletedAttachments: any[] = [];
    valid: Record<string, boolean | null> = {};
    configuratorId = 0;
    extId: string | null = null;
    fields: Record<string, ComplexConfiguratorField> = {};
    attributes = [];
    departments: Record<number,string> = {};
    sketchMainData: SketchData | null = null;
    sketchAdditionalData: string[] = [];
    productId: number = null;
    productExtId: string = null;
    code: string = null;
    productName: string = null;
    configuratorName: string = null;

    private converters = {
        8: changesInVersion8
    };

    constructor(
        configuration?,
        public dataRequiredToUpdate?: any,
        conversion = true,
    ) {
        if (ComplexConfiguration.is(configuration) && conversion) {
            this.runConversion(configuration, dataRequiredToUpdate);
        } else {
            if (ComplexActiveConfiguration.is(configuration)) {
                this.setConfiguration(configuration);
            }
        }
    }

    public static is(configuration): configuration is ComplexConfiguration {
        return configuration instanceof ComplexConfiguration || configuration.$version && configuration.type === 'complex';
    }

    setConfiguration(configuration){
        this.name = configuration?.name ? configuration.name : configuration.Name ? configuration.Name : null;
        this.price = configuration?.price ? configuration.price : configuration?.Price ? configuration.Price : null;
        this.priceNoMargin = configuration?.priceNoMargin ? configuration.priceNoMargin : configuration?.PriceNoMargin ? configuration.PriceNoMargin : this.price;
        this.priceAfterMultiplier = configuration.PriceAfterMultiplier || this.price;
        this.priceAfterMultiplierNoMargin = configuration.PriceAfterMultiplierNoMargin || this.priceNoMargin;
        this.priceAfterDiscounts = configuration?.priceAfterDiscounts ? configuration.priceAfterDiscounts : configuration?.PriceAfterDiscounts ? configuration.PriceAfterDiscounts : this.price;
        this.priceIsGross = configuration.PriceIsGross || false;
        this.priceTaxRate = configuration.PriceTaxRate || 0;
        this.quantity = configuration.Quantity || configuration.quantity;
        this.priceSegments = configuration?.priceSegments ? configuration.priceSegments : configuration?.PriceSegments ? configuration.PriceSegments : null ;
        this.priceSegmentsNoMargin = configuration?.priceSegmentsNoMargin ? configuration.priceSegmentsNoMargin : configuration?.PriceSegmentsNoMargin ? configuration.PriceSegmentsNoMargin: null;
        this.priceParts = configuration?.priceParts ? configuration.priceParts : configuration?.PriceParts ? configuration.PriceParts : null;
        this.pricePartsNoMargin = configuration?.pricePartsNoMargin ? configuration.pricePartsNoMargin : configuration?.PricePartsNoMargin ? configuration.PricePartsNoMargin :null;
        this.discountGroups = configuration.DiscountGroups;
        this.discountGroupsNoMargin = configuration.DiscountGroupsNoMargin;
        this.pricesRevisions = configuration.PricesRevisions || configuration.pricesRevisions || {
            prices: 0,
            priceRasters: 0,
        };
        this.title = configuration.Title || configuration.title;
        this.description = configuration.Description || configuration.description;
        this.attachments = configuration.Attachments || configuration.attachments;
        this.valid = configuration.valid;
        this.timeLimit = configuration.timeLimit;
        this.timeLimitsStack = configuration.timeLimitsStack;
        this.attributes = configuration.attributes;
        this.configuratorId = configuration.configuratorId;
        this.extId = configuration.extId;
        this.fields = configuration.fields;
        this.departments = configuration.departments;
        this.productId = configuration.productId;
        this.productExtId = configuration.productExtId;
        this.productName = configuration.productName;
        this.configuratorName = configuration.configuratorName;
        this.sketchMainData = configuration?.sketchMainData;
        this.sketchAdditionalData = configuration?.sketchAdditionalData;
    }

    runConversion(configuration, dataRequiredToUpdate: any) {
        let updatedConfiguration = core.copy(configuration);

        for (let version = configuration.$version + 1; version <= this.$version; version++) {
            updatedConfiguration.$version = version;
            if (this.converters[version] && typeof this.converters[version] === 'function') {
                updatedConfiguration = this.converters[version](
                    updatedConfiguration,
                    dataRequiredToUpdate
                );
            }
        }
        (Object as any)
            .entries(updatedConfiguration)
            .forEach(([key, value]) => (this[key] = value));
    }
}
